import { ArrowLeft, X } from 'lucide-react';
import { RISK_LEVELS } from '../../pages/Dashboard/TradeCopier/riskLevels';
import styles from './RiskLevelGuide.module.css';

/**
 * What each risk tolerance means, in one modal.
 *
 * Opened from the "Risk level guide" link in two places — the Start copying
 * journey's second step and the manage sheet — which is why it lives here
 * rather than inside either of them. Both open it over themselves: `onBack`
 * returns to whatever was underneath with its selection untouched, and
 * `onClose` leaves entirely.
 */
function RiskLevelGuide({ onBack, onClose }) {
  return (
    <div className={styles.overlay} onClick={(e) => e.target === e.currentTarget && onBack()}>
      <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Risk level guide">
        <div className={styles.header}>
          <button className={styles.backBtn} onClick={onBack} aria-label="Back">
            <ArrowLeft size={20} />
          </button>
          <h2 className={styles.title}>Risk level guide</h2>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        {/* The sections are the length they are, so the body scrolls rather than
            the modal growing past the screen. */}
        <div className={styles.body}>
          <p className={styles.intro}>
            Your risk tolerance sets how large the trades copied to your account are. It moves
            your daily target and your maximum daily loss together — a higher setting reaches a
            target faster and reaches your drawdown limit faster too.
          </p>

          {RISK_LEVELS.map((level) => (
            <section key={level.id} className={styles.level}>
              <h3 className={styles.levelName}>{level.label}</h3>

              <dl className={styles.figures}>
                <div className={styles.figure}>
                  <dt className={styles.figureLabel}>Daily target</dt>
                  <dd className={styles.figureValue}>{level.dailyTarget}</dd>
                </div>
                <div className={styles.figure}>
                  <dt className={styles.figureLabel}>Max daily loss</dt>
                  <dd className={`${styles.figureValue} ${styles.figureLoss}`}>{level.maxDailyLoss}</dd>
                </div>
              </dl>

              <p className={styles.levelText}>{level.guide}</p>

              {level.fullLossDays && (
                <p className={styles.levelDays}>
                  <strong>{level.fullLossDays}</strong> full-loss days before reaching max drawdown.
                </p>
              )}

              {level.note && <p className={styles.levelNote}>{level.note}</p>}
            </section>
          ))}

          <div className={styles.important}>
            <p className={styles.importantTitle}>Important</p>
            <p className={styles.importantText}>
              These figures are worked out from your account&rsquo;s starting balance and the
              maximum daily loss each setting allows — the full-loss day counts assume every one
              of those days loses the maximum. They describe how a setting behaves, not what it
              will earn: targets are not guaranteed, and past performance does not guarantee
              future results.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RiskLevelGuide;
